import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { getLowCreditsState, getNextUpgradePlan, normalizePlanName } from '@workspace/global-utils';
import { authenticatedNextFetch } from '@/api/api-service';
import { plansModalOpenAtom } from '@/jotai/atom';
import { creditTopUpModalOpenAtom, insufficientCreditsAtom } from '@/lib/credit-guard';
import { Config } from '@/utils/config';

type QuotaUsageResponse = {
  aiCredits: {
    // Serialized as null for unlimited plans (JSON drops Infinity).
    remaining: number | null;
    planName: string;
    canTopUp: boolean;
  };
};

export const AI_CREDITS_QUERY_KEY = ['ai-credits'];

const isGuest = Config.APP_RUN_TYPE === 'guest';

export const fetchAiCredits = async () => {
  const response = await authenticatedNextFetch(
    '/api/quota/usage',
    {},
    {
      onInsufficientCredits: 'ignore',
    },
  );
  if (!response.ok) throw new Error('Failed to load AI credits');
  return ((await response.json()) as QuotaUsageResponse).aiCredits;
};

/**
 * Credit status for the low-credit pill and banner. Null unless a metered plan is at or
 * below the low threshold of its plan allowance.
 */
export function useAiCredits() {
  const insufficientCredits = useAtomValue(insufficientCreditsAtom);
  const setTopUpOpen = useSetAtom(creditTopUpModalOpenAtom);
  const setPlansOpen = useSetAtom(plansModalOpenAtom);

  const { data, refetch } = useQuery({
    queryKey: AI_CREDITS_QUERY_KEY,
    queryFn: fetchAiCredits,
    enabled: !isGuest,
    staleTime: Config.AI_CREDITS.REFETCH_INTERVAL_MS,
    refetchInterval: Config.AI_CREDITS.REFETCH_INTERVAL_MS,
  });

  // A blocked AI action means the balance just ran out; show it without waiting for the poll.
  useEffect(() => {
    if (insufficientCredits) void refetch();
  }, [insufficientCredits, refetch]);

  if (!data || data.remaining === null) return null;

  const low = getLowCreditsState(data.remaining, data.planName);
  if (!low) return null;

  return {
    remaining: low.remaining,
    limit: low.planCredits,
    isExhausted: low.remaining === 0,
    isFree: normalizePlanName(data.planName) === 'free',
    canTopUp: data.canTopUp,
    actionLabel: data.canTopUp ? 'Top up' : 'Upgrade',
    // Active paid plans top up; everyone else upgrades, so name the plan they'd move to.
    upgradePlan: data.canTopUp ? null : getNextUpgradePlan(data.planName),
    onAction: () => (data.canTopUp ? setTopUpOpen(true) : setPlansOpen(true)),
  };
}

export type AiCreditsStatus = NonNullable<ReturnType<typeof useAiCredits>>;
